/**
 * OnboardingCarousel Component
 * 
 * A reusable carousel for onboarding flows with Lottie animations,
 * progress dots, and navigation buttons.
 */

import React, { useState, useRef, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  Dimensions,
  ViewToken,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { OnboardingSlide } from "./OnboardingSlide";
import { OnboardingProgress } from "./OnboardingProgress";
import type { OnboardingSlide as OnboardingSlideType } from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface OnboardingCarouselProps {
  /** Array of slides to display */
  slides: OnboardingSlideType[];
  /** Text for the final button */
  finishButtonText: string;
  /** Callback when user completes the onboarding */
  onComplete: () => void;
  /** Optional: show back button */
  showBackButton?: boolean;
  /** Optional: callback for back button */
  onBack?: () => void;
}

/**
 * Reusable onboarding carousel component
 */
export const OnboardingCarousel: React.FC<OnboardingCarouselProps> = ({
  slides,
  finishButtonText,
  onComplete,
  showBackButton = false,
  onBack,
}) => {
  const colors = useThemeColors();
  const [currentIndex, setCurrentIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);

  const isLastSlide = currentIndex === slides.length - 1;
  const isFirstSlide = currentIndex === 0;

  /**
   * Handle viewable items change
   */
  const handleViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems.length > 0 && viewableItems[0].index !== null) {
        setCurrentIndex(viewableItems[0].index);
      }
    },
    []
  );

  const viewabilityConfig = useRef({
    viewAreaCoveragePercentThreshold: 50,
  }).current;

  /**
   * Navigate to next slide
   */
  const handleNext = useCallback(() => {
    if (isLastSlide) {
      onComplete();
    } else {
      flatListRef.current?.scrollToIndex({
        index: currentIndex + 1,
        animated: true,
      });
    }
  }, [currentIndex, isLastSlide, onComplete]);

  /**
   * Navigate to previous slide
   */
  const handlePrevious = useCallback(() => {
    if (!isFirstSlide) {
      flatListRef.current?.scrollToIndex({
        index: currentIndex - 1,
        animated: true,
      });
    }
  }, [currentIndex, isFirstSlide]);

  /**
   * Render a single slide
   */
  const renderSlide = useCallback(
    ({ item, index }: { item: OnboardingSlideType; index: number }) => (
      <OnboardingSlide 
        slide={item} 
        isActive={index === currentIndex} 
      />
    ),
    [currentIndex]
  );

  return (
    <SafeAreaView 
      className="flex-1"
      style={{ backgroundColor: colors.background }}
    >
      {/* Header with optional back button */}
      {showBackButton && (
        <View className="px-4 pt-2 flex-row items-center">
          <TouchableOpacity
            onPress={onBack}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.surfaceSecondary }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="close" size={22} color={colors.text} />
          </TouchableOpacity>
        </View>
      )}

      {/* Carousel */}
      <FlatList
        ref={flatListRef}
        data={slides}
        renderItem={renderSlide}
        keyExtractor={(item) => item.id}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onViewableItemsChanged={handleViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        scrollEventThrottle={16}
        bounces={false}
        getItemLayout={(_, index) => ({
          length: SCREEN_WIDTH,
          offset: SCREEN_WIDTH * index,
          index,
        })}
      />

      {/* Bottom Section */}
      <View className="px-6 pb-4">
        {/* Progress Dots */}
        <OnboardingProgress
          total={slides.length}
          current={currentIndex}
          activeColor={colors.primary}
          inactiveColor={colors.border}
        />

        {/* Navigation Buttons */}
        <View className="flex-row items-center mt-4">
          {/* Previous Button */}
          <TouchableOpacity
            onPress={handlePrevious}
            disabled={isFirstSlide}
            className="w-14 h-14 rounded-full items-center justify-center mr-4"
            style={{
              backgroundColor: isFirstSlide 
                ? colors.surfaceSecondary 
                : `${colors.primary}15`,
              opacity: isFirstSlide ? 0.5 : 1,
            }}
            accessibilityRole="button"
            accessibilityLabel="Vorige"
          >
            <Ionicons
              name="chevron-back"
              size={24}
              color={isFirstSlide ? colors.textTertiary : colors.primary}
            />
          </TouchableOpacity>

          {/* Next / Complete Button */}
          <TouchableOpacity
            onPress={handleNext}
            className="flex-1 h-14 rounded-full items-center justify-center flex-row"
            style={{ backgroundColor: colors.primary }}
            accessibilityRole="button"
            accessibilityLabel={isLastSlide ? finishButtonText : "Volgende"}
          >
            <Text className="text-white font-bold text-lg mr-2">
              {isLastSlide ? finishButtonText : "Volgende"}
            </Text>
            {!isLastSlide && (
              <Ionicons name="chevron-forward" size={20} color="#FFFFFF" />
            )}
            {isLastSlide && (
              <Ionicons name="checkmark" size={22} color="#FFFFFF" />
            )}
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

export default OnboardingCarousel;

